import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { LanguageContext } from "App";
import AccountingApi from "../../../Classes/API/AccountingApi";
import CreateAccountingEntryReq from "../../../Classes/Models/Requests/Accounting/CreateAccountingEntryReq";

// Custom hooks
import { useAccountingData } from "./hooks/useAccountingData";
import { useEntryForm } from "./hooks/useEntryForm";
import { useReceiptDownload } from "./hooks/useReceiptDownload";

// Components
import EntryTypeSelect from "./components/EntryTypeSelect";
import PaymentMethodSelect from "./components/PaymentMethodSelect";
import CategorySelect from "./components/CategorySelect";
import EntrySpecificSelect from "./components/EntrySpecificSelect";

// Utils
import { validateForm } from "./utils/validation";
import {
  getAutoGeneratedDescription,
  getAutoGeneratedTitle,
  getCategoryForEntryType,
} from "./utils/entryHelpers";
import {
  EntryTypes,
  RECEIPT_ENTRY_TYPES,
  EXPENSE_CATEGORIES,
} from "./utils/constants";

const CreateAccountingEntry = () => {
  const { translations } = useContext(LanguageContext);
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  // Custom hooks
  const { formData, updateFormField, CurrentUser } = useEntryForm();
  const accountingData = useAccountingData();
  const { promptAndDownloadReceipt } = useReceiptDownload();

  // Handle initial state from navigation
  useEffect(() => {
    if (!state) {
      updateFormField("entryType", EntryTypes.Expense);
      return;
    }

    const { entryType, ...stateData } = state;
    updateFormField("entryType", entryType);

    switch (entryType) {
      case EntryTypes.AccSession:
        initializeAccSessionEntry(stateData.AccSession);
        break;
      case EntryTypes.Package:
        initializePackageEntry(stateData.Package);
        break;
      case EntryTypes.AccThHour:
        initializeAccThHourEntry(stateData.AccThHour);
        break;
      case EntryTypes.UserBalance:
        initializeUserBalanceEntry(stateData.User);
        break;
      default:
        break;
    }
  }, [state]);

  const initializeAccSessionEntry = (accSession) => {
    updateFormField("title", getAutoGeneratedTitle(EntryTypes.AccSession));
    updateFormField(
      "description",
      getAutoGeneratedDescription(
        EntryTypes.AccSession,
        accSession,
        CurrentUser,
      ),
    );
    updateFormField("category", getCategoryForEntryType(EntryTypes.AccSession));
    updateFormField("accountingSession", accSession);
    updateFormField("amount", accSession.finalPrice);
  };

  const initializePackageEntry = (packageData) => {
    updateFormField("title", getAutoGeneratedTitle(EntryTypes.Package));
    updateFormField(
      "description",
      getAutoGeneratedDescription(EntryTypes.Package, packageData, CurrentUser),
    );
    updateFormField("category", getCategoryForEntryType(EntryTypes.Package));
    updateFormField("package", packageData);
    updateFormField("amount", packageData.finalPrice);
  };

  const initializeAccThHourEntry = (accThHour) => {
    updateFormField("title", getAutoGeneratedTitle(EntryTypes.AccThHour));
    updateFormField(
      "description",
      getAutoGeneratedDescription(EntryTypes.AccThHour, accThHour, CurrentUser),
    );
    updateFormField("category", getCategoryForEntryType(EntryTypes.AccThHour));
    updateFormField("accountingTherapistHourId", accThHour.id);
    updateFormField("amount", accThHour.finalPrice);
  };

  const initializeUserBalanceEntry = (user) => {
    updateFormField("user", user);
  };

  // Update amount when salary entry type is selected
  useEffect(() => {
    if (formData.entryType === EntryTypes.Salary && formData.user?.salary) {
      updateFormField("amount", formData.user.salary);
    }
  }, [formData.entryType, formData.user]);

  // Set default expense category
  useEffect(() => {
    if (
      formData.entryType === EntryTypes.Expense &&
      !EXPENSE_CATEGORIES.includes(formData.category)
    ) {
      updateFormField("category", EXPENSE_CATEGORIES[0]);
    }
  }, [formData.entryType, formData.category]);

  const handleSubmit = async () => {
    if (!validateForm(formData, translations)) return;

    const result = await Swal.fire({
      title: "Confirm Entry",
      text: `Are you sure you want to create this ${formData.entryType} entry for ${formData.amount}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, create it",
      cancelButtonText: "Review again",
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    try {
      const model = new CreateAccountingEntryReq();
      Object.assign(model, {
        title: formData.title,
        accountingSessionId: formData.accountingSession?.id,
        accountingTherapistHourId: formData.accountingTherapistHourId,
        userId: formData.user?.id,
        creatorId: CurrentUser.id,
        packageId: formData.package?.id,
        amount: formData.amount,
        category: formData.category,
        description: formData.description,
        entryType: formData.entryType,
        paymentMethod: formData.paymentMethod,
        beneficiaryEntity: formData.beneficiaryEntity,
      });

      const res = await AccountingApi.CreateAccountingEntry(model);

      if (res.status === "Success") {
        await Swal.fire({
          title: "Success!",
          text: "Entry has been created successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        if (RECEIPT_ENTRY_TYPES.includes(formData.entryType) && res.data > 0) {
          await promptAndDownloadReceipt(res.data);
        }
      } else {
        throw new Error(res.message);
      }
    } catch (error) {
      await Swal.fire({
        title: "Error",
        text: error.message || "An unexpected error occurred",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      setLoading(false);
    }
  };

  if (accountingData.isLoading) {
    return <div>LOADING...</div>;
  }

  return (
    <MDBox mt={6} mb={3}>
      <div className="card">
        <div className={translations.setlang === "ar" ? "rtl-label" : ""}>
          <MDBox
            pt={2}
            px={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" fontWeight="medium">
              {translations.CreateAccountingEntrypg}
            </MDTypography>
            <MDButton
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                backgroundColor: loading ? "#ccc" : "#3560AD",
                "&:hover": { backgroundColor: "#4682B4" },
              }}
              color="success"
            >
              {loading ? "Saving..." : translations.createmultiPackageCreate}
            </MDButton>
          </MDBox>

          <div className="container-fluid mt-3">
            <div className="row">
              {/* Title */}
              <div className="col-md-4 mt-2">
                <MDInput
                  value={formData.title}
                  className="full-width"
                  label={translations.CreateAccountingEntrypgtitle}
                  onChange={(e) => updateFormField("title", e.target.value)}
                  fullWidth
                />
              </div>

              {/* Amount */}
              <div className="col-md-4 mt-2">
                <MDInput
                  value={formData.amount}
                  className="full-width"
                  type="number"
                  onChange={(e) => updateFormField("amount", e.target.value)}
                  label={translations.CreateAccountingEntrypgAmount}
                  fullWidth
                />
                <span className="text-success">
                  {formData.amount && `Selected Amount: EGP${formData.amount}`}
                </span>
              </div>

              {/* Category */}
              <div className="col-md-4 mt-2">
                <CategorySelect
                  value={formData.category}
                  onChange={(value) => updateFormField("category", value)}
                  entryType={formData.entryType}
                  translations={translations}
                />
              </div>

              {/* Entry Type */}
              <div className="col-md-4 mt-4">
                <EntryTypeSelect
                  value={formData.entryType}
                  onChange={(value) => updateFormField("entryType", value)}
                  translations={translations}
                />
              </div>

              {/* Payment Method */}
              <div className="col-md-4 mt-4">
                <PaymentMethodSelect
                  value={formData.paymentMethod}
                  onChange={(value) => updateFormField("paymentMethod", value)}
                  translations={translations}
                />
              </div>

              {/* Beneficiary Entity */}
              <div className="col-md-4 mt-4">
                <MDInput
                  value={formData.beneficiaryEntity}
                  className="full-width"
                  label={translations.SearchEntrybeneficiary}
                  onChange={(e) =>
                    updateFormField("beneficiaryEntity", e.target.value)
                  }
                  fullWidth
                />
              </div>

              {/* Entry Specific Select (User, Package, Session, etc.) */}
              <EntrySpecificSelect
                entryType={formData.entryType}
                formData={formData}
                updateFormField={updateFormField}
                accountingData={accountingData}
                translations={translations}
                state={state}
              />

              {/* Description */}
              <div className="col-md-12 mt-4">
                <textarea
                  value={formData.description}
                  className="form-control h-60"
                  placeholder={translations.CreateAccountingEntrypgdescription}
                  onChange={(e) =>
                    updateFormField("description", e.target.value)
                  }
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MDBox>
  );
};

export default CreateAccountingEntry;
